import viewable from 'in-view'
import loadjs from 'loadjs'
import set from 'lodash.set'
import IVideoConfig from './@types/IVideoConfig'
import IVideoOptions from './@types/IVideoOptions'
import {
  AD_REPORTER_HEIGHT,
  BORDER_RADIUS,
  STICKY_TITLE_HEIGHT,
  TARGET_DIV_CLASS
} from './constants/css'
import AppEvent from './enums/AppEvent'
import PlayerOptions from './helpers/PlayerOptions'
import { record } from './helpers/tracker'

// TODO: remove when `web-wrapper` replaces references with `window.$mediavine.video`
set(window, 'MediavineVideo.placement.render', render) // (Deprecated)
set(window, '$mediavine.video.placement.render', render)

export function render(
  config: IVideoConfig,
  manualOptions: Partial<IVideoOptions>
): void {
  try {
    record(AppEvent.initPlacement, config)

    const options = new PlayerOptions(config, manualOptions)
    const { targetDiv, uuid } = options
    const percentHeight = (1 / options.aspectFraction) * 100
    const onPageActive = () => {
      window.removeEventListener('scroll', onPageActive)
      loadjs(`${config.playerSource}/player.js`, () => {
        window.MediavineVideo.player.render(options).catch(console.error)
      })
    }

    if (targetDiv && targetDiv.childElementCount === 0) {
      targetDiv.classList.add(TARGET_DIV_CLASS)
      targetDiv.style.position = 'relative'

      // Setup target div child containers (placeholder and app)
      const container = addContainer(targetDiv, uuid, percentHeight)
      const backgroundContainer = addBackgroundContainer(container, uuid)
      addAppContainer(container, uuid)
      lazyLoadBackground(backgroundContainer, options.srcPoster)
    } else {
      // Maybe was already filled by another script
      return
    }

    if (viewable.is(targetDiv)) {
      onPageActive()
    } else {
      window.addEventListener('scroll', onPageActive)
    }
  } catch (e) {
    console.error(
      'Problem rendering Mediavine Video placement:',
      config.video.slug,
      e
    )
  }
}

function addContainer(
  el: HTMLElement,
  id: string,
  percentHeight: number
): HTMLDivElement {
  const newEl: HTMLDivElement = document.createElement('div')
  const extraHeight = AD_REPORTER_HEIGHT + STICKY_TITLE_HEIGHT

  newEl.id = `${id}_container`
  newEl.style.borderRadius = `${BORDER_RADIUS}px`
  newEl.style.margin = '0 1px' // allow border to be visible on mobile
  newEl.style.overflow = 'hidden'
  newEl.style.paddingTop = `calc(${percentHeight}% + ${extraHeight}px)`
  newEl.style.position = 'relative'

  el.appendChild(newEl)

  return newEl
}

function addBackgroundContainer(el: HTMLElement, id: string): HTMLDivElement {
  const newEl: HTMLDivElement = document.createElement('div')

  newEl.id = `${id}_background-container`
  newEl.style.backgroundRepeat = 'no-repeat'
  newEl.style.backgroundSize = 'cover'
  newEl.style.backgroundColor = 'transparent'
  newEl.style.borderRadius = `${BORDER_RADIUS}px`
  newEl.style.overflow = 'hidden'
  newEl.style.position = 'absolute'
  newEl.style.bottom = `${AD_REPORTER_HEIGHT + BORDER_RADIUS}px`
  newEl.style.left = '0'
  newEl.style.right = '0'
  newEl.style.top = `${STICKY_TITLE_HEIGHT}px`

  el.appendChild(newEl)

  return newEl
}

function addAppContainer(el: HTMLElement, id: string): HTMLDivElement {
  const newEl: HTMLDivElement = document.createElement('div')

  newEl.id = `${id}_app-container`
  newEl.style.position = 'absolute'
  newEl.style.bottom = '0'
  newEl.style.left = '0'
  newEl.style.right = '0'
  newEl.style.top = '0'

  el.appendChild(newEl)

  return newEl
}

function lazyLoadBackground(
  el: HTMLElement,
  imgUrl: string,
  offset: number = window.innerHeight
): void {
  if (IntersectionObserver) {
    const observerConfig = {
      rootMargin: `0px 0px ${offset}px 0px`,
      threshold: 0
    }

    const observer = new IntersectionObserver((entries, self) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          el.style.backgroundImage = `url("${imgUrl}")`
          self.unobserve(entry.target)
        }
      })
    }, observerConfig)

    observer.observe(el)
  }
}
